import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { ApiError, humanError, observeExchanges, type ApiExchange } from "../shared/api/client";
import { getSession, lock } from "../features/session/api";
import { getPreferences } from "../features/workspace/api";
import type { Currency } from "../entities/ledger/types";
import { AccountPage } from "../pages/AccountPage";
import { AccountsPage } from "../pages/AccountsPage";
import { BooksPage } from "../pages/BooksPage";
import { DashboardPage } from "../pages/DashboardPage";
import { PlanningPage } from "../pages/PlanningPage";
import { BorrowingsPage } from "../pages/BorrowingsPage";
import { JournalPage } from "../pages/journal/JournalPage";
import { SetupPage } from "../pages/SetupPage";
import { UnlockPage } from "../pages/UnlockPage";
import { Layout } from "../shared/ui/Layout";
import { LoadingPanel } from "../shared/ui/PageState";
import { Toast } from "../shared/ui/Toast";

async function apiHealth(): Promise<boolean> {
  try {
    const response = await fetch("/api/healthz", { cache: "no-store" });
    return response.ok;
  } catch {
    return false;
  }
}

export function App() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [exchange, setExchange] = useState<ApiExchange | null>(null);
  const [toast, setToast] = useState("");
  const toastTimer = useRef<number | null>(null);
  // Probed every few minutes: the Worker rotates the gateway session ahead of
  // the 15-minute access expiry on this single request, not on a burst of
  // parallel API calls.
  const session = useQuery({ queryKey: ["session"], queryFn: getSession, staleTime: 30_000, refetchInterval: 4 * 60_000, refetchIntervalInBackground: true });
  const health = useQuery({ queryKey: ["health"], queryFn: apiHealth, refetchInterval: 60_000 });
  const preferences = useQuery({
    queryKey: ["preferences"],
    queryFn: getPreferences,
    enabled: session.data?.authenticated === true,
    retry: false,
  });
  const lockMutation = useMutation({
    mutationFn: lock,
    onSuccess: () => {
      const mode = session.data?.mode ?? "code";
      queryClient.clear();
      queryClient.setQueryData(["session"], { authenticated: false, mode });
      navigate("/unlock", { replace: true });
    },
  });

  useEffect(() => observeExchanges(setExchange), []);
  useEffect(() => () => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
  }, []);

  function notify(message: string) {
    setToast(message);
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 2600);
  }

  function authenticated() {
    queryClient.setQueryData(["session"], { authenticated: true, mode: session.data?.mode ?? "code" });
    void queryClient.invalidateQueries({ queryKey: ["preferences"] });
    navigate("/", { replace: true });
  }

  function workspaceReady(currency: Currency) {
    queryClient.setQueryData(["preferences"], { defaultCurrency: currency });
    void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    navigate("/", { replace: true });
  }

  if (session.isPending) {
    return <Layout apiOnline={health.data ?? null}><section className="view full-state"><p className="eyebrow">Ledgerly</p><h2>Opening your workspace…</h2></section></Layout>;
  }

  const isAuthenticated = session.data?.authenticated === true;
  if (!isAuthenticated) {
    return (
      <Layout apiOnline={health.data ?? null}>
        <Routes>
          <Route path="/unlock" element={<UnlockPage onUnlocked={authenticated} mode={session.data?.mode} />} />
          {/* The gateway redirects failed Telegram sign-ins to /login?error=… */}
          <Route path="/login" element={<UnlockPage onUnlocked={authenticated} mode={session.data?.mode} />} />
          <Route path="*" element={<Navigate to="/unlock" replace />} />
        </Routes>
        <Toast message={toast} />
      </Layout>
    );
  }

  const preferencesMissing = preferences.error instanceof ApiError && preferences.error.status === 404;
  let routes;
  if (preferences.isPending) {
    routes = <section className="view"><div className="page-heading"><div><p className="eyebrow">Ledgerly</p><h1>Opening your workspace</h1></div></div><LoadingPanel message="Loading preferences…" /></section>;
  } else if (preferencesMissing) {
    routes = (
      <Routes>
        <Route path="/setup" element={<SetupPage onReady={workspaceReady} onNotify={notify} />} />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    );
  } else if (preferences.isError || !preferences.data) {
    // The navigation stays mounted below, so this is one failed block on an
    // otherwise working shell rather than a dead end.
    routes = (
      <section className="view" aria-labelledby="preferences-error">
        <div className="page-heading"><div><p className="eyebrow">Ledgerly</p><h1 id="preferences-error">Your workspace did not load</h1></div></div>
        <section className="panel state-panel" role="alert">
          <p className="eyebrow">Could not load preferences</p>
          <h2>{preferences.error ? humanError(preferences.error) : "Ledgerly could not read your preferences."}</h2>
          <p className="panel-note">Nothing was changed. Your books and journal are intact; only this request failed. The sections above still work.</p>
          <div className="form-actions">
            <button className="primary-button compact" type="button" onClick={() => preferences.refetch()}><span>Try again</span><span aria-hidden="true">↻</span></button>
          </div>
        </section>
      </section>
    );
  } else {
    routes = (
      <Routes>
        <Route path="/" element={<DashboardPage currency={preferences.data.defaultCurrency} exchange={exchange} onNotify={notify} />} />
        <Route path="/accounts" element={<AccountsPage currency={preferences.data.defaultCurrency} creating={false} onNotify={notify} />} />
        <Route path="/accounts/new" element={<AccountsPage currency={preferences.data.defaultCurrency} creating onNotify={notify} />} />
        <Route path="/accounts/:accountId" element={<AccountPage currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="/journal" element={<JournalPage view="entries" currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="/journal/statement" element={<JournalPage view="statement" currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="/journal/categories" element={<JournalPage view="categories" currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="/journal/record" element={<JournalPage view="record" currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        {/* The journal replaced the single transactions page; old links still resolve. */}
        <Route path="/transactions" element={<Navigate to="/journal" replace />} />
        <Route path="/planning" element={<PlanningPage view="items" currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="/planning/groups" element={<PlanningPage view="groups" currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="/planning/budgets" element={<PlanningPage view="budgets" currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="/planning/rates" element={<PlanningPage view="rates" currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="/borrowings" element={<BorrowingsPage currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="/borrowings/:borrowingId" element={<BorrowingsPage detail currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="/books" element={<BooksPage currency={preferences.data.defaultCurrency} onNotify={notify} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Layout apiOnline={health.data ?? null} onLock={() => lockMutation.mutate()} locking={lockMutation.isPending} navigation={!preferencesMissing && !preferences.isPending}>
      {routes}
      <Toast message={toast} />
    </Layout>
  );
}
